import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, Play, Pause, SkipBack, SkipForward, 
  Clock, Maximize, Minimize, X, Music, ChevronLeft, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetlistPresentation = () => {
  const navigate = useNavigate();
  const { setlistId } = useParams();
  
  const [setlistData, setSetlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [autoPlay, setAutoPlay] = useState(false);
  const [autoScrollSpeed, setAutoScrollSpeed] = useState(5);
  const [progress, setProgress] = useState(0);
  
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);

  const fetchSetlistSlides = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/setlists/${setlistId}/slides`);
      setSetlistData(response.data);
      setAutoScrollSpeed(response.data.auto_scroll_speed || 5);
    } catch (error) {
      toast.error("Failed to load setlist");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [setlistId, navigate]);

  useEffect(() => {
    fetchSetlistSlides();
  }, [fetchSetlistSlides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowRight":
        case " ":
        case "Enter":
          e.preventDefault();
          nextSlide();
          break;
        case "ArrowLeft":
          e.preventDefault();
          prevSlide();
          break;
        case "Escape":
          if (isFullscreen) {
            exitFullscreen();
          } else {
            navigate("/dashboard");
          }
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
        case "p":
        case "P":
          setAutoPlay(prev => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, currentIndex, setlistData?.slides?.length]);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && setlistData?.slides?.length > 1) {
      setProgress(0);
      
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) return 0;
          return prev + (100 / (autoScrollSpeed * 10));
        });
      }, 100);

      autoPlayIntervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= setlistData.slides.length - 1) {
            return 0;
          }
          return prev + 1;
        });
        setProgress(0);
      }, autoScrollSpeed * 1000);
    }

    return () => {
      if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [autoPlay, autoScrollSpeed, setlistData?.slides?.length]);

  // Hide controls after inactivity
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        if (isFullscreen) setShowControls(false);
      }, 3000);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isFullscreen]);

  const nextSlide = useCallback(() => {
    if (setlistData?.slides && currentIndex < setlistData.slides.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
    }
  }, [currentIndex, setlistData?.slides]);

  const prevSlide = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setProgress(0);
    }
  }, [currentIndex]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    setIsFullscreen(false);
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Calculate overall progress through setlist
  const overallProgress = setlistData?.slides?.length 
    ? ((currentIndex + 1) / setlistData.slides.length) * 100 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white animate-pulse">Loading setlist...</div>
      </div>
    );
  }

  if (!setlistData?.slides?.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <p className="mb-4">No slides in this setlist</p>
        <Button onClick={() => navigate("/dashboard")} variant="outline">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const currentSlide = setlistData.slides[currentIndex];

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-black presentation-mode relative overflow-hidden"
      data-testid="setlist-presentation-container"
    >
      {/* Slide Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 flex items-center justify-center"
          style={{ 
            backgroundColor: currentSlide.background_color,
            color: currentSlide.text_color
          }}
          onClick={() => setShowControls(!showControls)}
          data-testid="current-setlist-slide"
        >
          <div className="max-w-5xl px-8 text-center">
            {/* Song separator indicator */}
            {currentSlide.is_separator && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-full">
                  <Music className="w-5 h-5" />
                  <span className="font-semibold">NOW PLAYING</span>
                </div>
              </motion.div>
            )}

            {/* Type Badge */}
            {!currentSlide.is_separator && currentSlide.type !== 'text' && currentSlide.type !== 'title' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-8"
              >
                <span className={currentSlide.type === 'chorus' ? 'chorus-badge text-lg' : 'verse-badge text-lg'}>
                  {currentSlide.type.toUpperCase()}
                </span>
              </motion.div>
            )}
            
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <p 
                className="font-lyrics leading-relaxed whitespace-pre-line"
                style={{ 
                  fontSize: `clamp(24px, ${currentSlide.font_size / 12}vw, ${currentSlide.font_size * 1.5}px)`,
                  letterSpacing: '0.02em'
                }}
              >
                {currentSlide.content}
              </p>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Auto-scroll Progress Bar */}
      {autoPlay && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <motion.div 
            className="h-full bg-gradient-to-r from-amber-500 to-amber-600"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Overall Setlist Progress */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-20">
        <div 
          className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 transition-all duration-300"
          style={{ width: `${overallProgress}%` }}
        />
      </div>

      {/* Top Controls Bar */}
      <motion.div
        className={`absolute top-1 left-0 right-0 p-4 flex items-center justify-between transition-opacity duration-300 z-10 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)' }}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="text-white hover:bg-white/20"
          data-testid="exit-setlist-btn"
        >
          <X className="w-6 h-6" />
        </Button>

        <div className="flex flex-col items-center">
          <span className="text-white text-sm font-medium">
            {setlistData.setlist_name}
          </span>
          {currentSlide.project_name && !currentSlide.is_separator && (
            <span className="text-amber-400 text-xs">
              {currentSlide.project_name}
            </span>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleFullscreen}
          className="text-white hover:bg-white/20"
          data-testid="fullscreen-setlist-btn"
        >
          {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
        </Button>
      </motion.div>

      {/* Bottom Controls */}
      <motion.div
        className={`absolute bottom-0 left-0 right-0 p-6 transition-opacity duration-300 z-10 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Main Controls */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="text-white hover:bg-white/20 disabled:opacity-30"
              data-testid="setlist-prev-btn"
            >
              <SkipBack className="w-6 h-6" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentIndex(0)}
              className="text-white hover:bg-white/20"
              data-testid="setlist-restart-btn"
            >
              <ChevronLeft className="w-6 h-6" />
              <ChevronLeft className="w-6 h-6 -ml-4" />
            </Button>

            <Button
              size="lg"
              onClick={() => setAutoPlay(!autoPlay)}
              className={`rounded-full w-16 h-16 ${
                autoPlay 
                  ? 'bg-amber-500 hover:bg-amber-600 text-black' 
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              data-testid="setlist-play-pause-btn"
            >
              {autoPlay ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentIndex(setlistData.slides.length - 1)}
              className="text-white hover:bg-white/20"
              data-testid="setlist-end-btn"
            >
              <ChevronRight className="w-6 h-6" />
              <ChevronRight className="w-6 h-6 -ml-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={nextSlide}
              disabled={currentIndex === setlistData.slides.length - 1}
              className="text-white hover:bg-white/20 disabled:opacity-30"
              data-testid="setlist-next-btn"
            >
              <SkipForward className="w-6 h-6" />
            </Button>
          </div>

          {/* Slide Counter & Speed Control */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-white/70 text-sm">
                Slide {currentIndex + 1} of {setlistData.slides.length}
              </span>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-white/70" />
                <Slider
                  value={[autoScrollSpeed]}
                  min={2}
                  max={15}
                  step={1}
                  onValueChange={([value]) => setAutoScrollSpeed(value)}
                  className="w-24"
                  data-testid="setlist-speed-slider"
                />
                <span className="text-white/70 text-sm w-8">{autoScrollSpeed}s</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-white/70 text-sm">Auto</span>
                <Switch
                  checked={autoPlay}
                  onCheckedChange={setAutoPlay}
                  data-testid="setlist-auto-play-switch"
                />
              </div>
            </div>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="mt-4 flex items-center justify-center gap-6 text-white/40 text-xs">
            <span><kbd className="kbd">←</kbd> / <kbd className="kbd">→</kbd> Navigate</span>
            <span><kbd className="kbd">Space</kbd> Next</span>
            <span><kbd className="kbd">F</kbd> Fullscreen</span>
            <span><kbd className="kbd">P</kbd> Play/Pause</span>
            <span><kbd className="kbd">Esc</kbd> Exit</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SetlistPresentation;
